import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import RightModalLayout from '../../../../../../../layouts/Modal/RightModalLayout';
import CardMC from '../../../../../../../components/CardMC';
import ButtonMC from '../../../../../../../components/ButtonMC';
import LoadingMC from '../../../../../../../components/LoadingMC';
import DropdownMC from '../../../../../../../components/DropdownMC';
import IconMC from '../../../../../../../components/IconMC';
import { SIDEBAR_ACTIONS } from '../../../../../../../../constants/actionsConstants';

import MiaAgentModel from '../../../../../../../models/MiaAgentModel';
import { showtoast } from '../../../../../../../../util/toast';
import { useTools } from '../../../hooks/useTools';
import { AgentProvider } from '../../AgentContext';
import { editorHtmlToTokenPlainText, normalizeInstructionDescription } from '../../utils/instructionDescriptionTokens';

const COMPONENT_KEY = 'IntentionTemplateModal';

const normalizeTemplateList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.templates)) return payload.templates;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const renderStepDescriptionWithPills = (step, getToolDetails, t) => {
  const text = editorHtmlToTokenPlainText(step?.description || '');
  if (!text) return null;

  const parts = text.split(/(\{\{[^{}]+\}\})/g);

  return parts.map((part, index) => {
    const partKey = `${step?.id || 'step'}-${index}`;
    const match = part.match(/\{\{([^:]+):(\d+)\}\}/);
    if (match) {
      const toolName = match[1];
      const toolIndex = parseInt(match[2], 10);
      const tool = step.tools && step.tools[toolIndex];
      if (tool && tool.name === toolName) {
        const toolDetails = getToolDetails(tool.name);
        if (toolDetails) {
          return (
            <span key={partKey} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-px-2 tw-py-[2px] tw-mx-1 tw-align-middle">
              <div className={`tw-p-[2px] tw-rounded-md ${toolDetails.bgClass}`}>
                <IconMC name={toolDetails.icon} className={`${toolDetails.colorClass} !tw-text-[10px]`} size="sm" />
              </div>
              <span className="tw-text-2xs tw-text-gray-500">
                {t(toolDetails.nameKey)}
              </span>
            </span>
          );
        }

        return (
          <span key={partKey} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-px-2 tw-py-[2px] tw-mx-1 tw-align-middle">
            <span className="tw-text-2xs tw-text-gray-500">
              {tool.name}
            </span>
          </span>
        );
      }
    }
    return <span key={partKey}>{part}</span>;
  });
};

const IntentionTemplateModalContent = ({
  agentId,
  onIntentionSaved,
  onEditIntention
}) => {
  const { t } = useTranslation('ChatbotIA');
  const dispatch = useDispatch();
  const { getToolDetails } = useTools();

  const [loading, setLoading] = useState(true);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);

  const closeAction = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: COMPONENT_KEY }
    });
  }, [dispatch]);

  useEffect(() => {
    let alive = true;
    if (!agentId) {
      setLoading(false);
      setTemplates([]);
      return () => {};
    }

    (async () => {
      setLoading(true);
      setError(null);

      await MiaAgentModel.getIntentionTemplates({ id: agentId })
        .then((res) => {
          const list = normalizeTemplateList(res?.data || res);
          if (!alive) return;
          setTemplates(list);
        })
        .finally(() => {
          if (!alive) return;
          setLoading(false);
        });
    })();

    return () => {
      alive = false;
    };
  }, [agentId]);

  useEffect(() => {
    if (!templates.length) return;
    setSelectedId((prev) => prev ?? (templates[0]?.id || null));
  }, [templates]);

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => String(tpl.id) === String(selectedId)) || null,
    [templates, selectedId]
  );

  useEffect(() => {
    let alive = true;
    if (!agentId) return () => {};
    if (!selectedTemplate?.id) return () => {};

    // Fetch the full template (with steps) when selecting it,
    // so the preview can render immediately.
    if (Array.isArray(selectedTemplate.steps)) return () => {};

    (async () => {
      setLoadingTemplate(true);
      setError(null);

      await MiaAgentModel.getIntentionTemplate({ id: agentId }, selectedTemplate.id)
        .then((detail) => {
          const hydrated = detail?.data || detail || selectedTemplate;
          if (!alive) return;

          setTemplates((prev) => prev.map((tpl) => (
            String(tpl.id) === String(selectedTemplate.id) ? hydrated : tpl
          )));
        })
        .finally(() => {
          if (!alive) return;
          setLoadingTemplate(false);
        });
    })();

    return () => { alive = false; };
  }, [agentId, selectedTemplate?.id]); // intentionally refetch when selection changes

  const handleApply = useCallback(async () => {
    if (!selectedTemplate || applying) return;
    if (!agentId) return;

    setApplying(true);
    setError(null);

    (async () => {
      let template = selectedTemplate;
      if (!Array.isArray(template.steps)) {
        const detail = await MiaAgentModel.getIntentionTemplate({ id: agentId }, template.id);
        template = detail?.data || detail || template;
      }

      const createdIntention = await MiaAgentModel.saveIntention(
        { id: agentId },
        {
          name: template.name,
          description: template.description
        }
      );

      const steps = Array.isArray(template.steps) ? template.steps : [];
      const createdSteps = [];
      /* eslint-disable-next-line no-await-in-loop -- keep server order */
      for (let i = 0; i < steps.length; i += 1) {
        const step = steps[i];
        const payload = {
          intention_position: i + 1,
          description: normalizeInstructionDescription(step.description || ''),
          tools: step.tools || []
        };
        // eslint-disable-next-line no-await-in-loop -- each request returns the created step
        const created = await MiaAgentModel.saveIntentionStep(
          { id: agentId },
          createdIntention.id,
          payload
        );
        createdSteps.push(created);
      }

      const finalIntention = {
        ...createdIntention,
        steps: createdSteps
      };

      if (onIntentionSaved) onIntentionSaved(finalIntention);
      if (onEditIntention) onEditIntention(finalIntention);

      closeAction();
      showtoast(t('config.intention_templates_modal.success_apply'), 'success');
    })().finally(() => {
      setApplying(false);
    });
  }, [agentId, applying, closeAction, onEditIntention, onIntentionSaved, selectedTemplate, t]);

  return (
    <RightModalLayout
      closeAction={closeAction}
      title={t('config.intention_templates_modal.title')}
      showBackButton
      rootClassName="tw-bg-gray-50"
      footerClassName="!tw-bg-gray-50"
      className="tw-space-y-0"
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-text-xs tw-text-gray-600">
          {t('config.intention_templates_modal.subtitle')}
        </div>

        {loading ? (
          <CardMC className="tw-p-10 tw-flex-col tw-items-center tw-justify-center">
            <LoadingMC loading={loading} message={t('config.intention_templates_modal.loading')} />
          </CardMC>
        ) : templates.length === 0 ? (
          <CardMC className="tw-p-10 tw-flex-col tw-items-center tw-justify-center tw-text-center">
            <div className="tw-text-sm tw-font-medium tw-text-gray-900">
              {t('config.intention_templates_modal.empty_title')}
            </div>
            <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
              {t('config.intention_templates_modal.empty_description')}
            </div>
          </CardMC>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <DropdownMC
              id="intention_template"
              name="intention_template"
              label={t('config.intention_templates_modal.dropdown_label')}
              placeholder={t('config.intention_templates_modal.dropdown_placeholder')}
              options={templates.map((tpl) => ({
                id: String(tpl.id),
                label: tpl.name
              }))}
              defaultValue={selectedId != null ? String(selectedId) : undefined}
              onSelect={(val) => setSelectedId(val)}
              useFloatingLabel
            />
            {selectedTemplate?.description ? (
              <div className="tw-text-xs tw-text-gray-600">
                {selectedTemplate.description}
              </div>
            ) : null}
          </div>
        )}

        {(loadingTemplate && selectedTemplate) || selectedTemplate?.steps?.length ? (
          <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-text-xs tw-font-semibold tw-text-gray-900">
              {t('config.intention_templates_modal.preview_title')}
            </div>
            {loadingTemplate ? (
              <CardMC className="tw-p-10 tw-flex-col tw-items-center tw-justify-center">
                <LoadingMC loading={loadingTemplate} message={t('config.intention_templates_modal.loading')} />
              </CardMC>
            ) : (
              <CardMC className="tw-p-4 tw-bg-white">
            <ol className="tw-m-0 tw-p-0 tw-list-none tw-flex tw-flex-col tw-gap-2">
              {selectedTemplate.steps.slice(0, 6).map((st, idx) => (
                <li key={`${selectedTemplate.id}-preview-${idx}`} className="tw-flex tw-gap-2">
                  <div className="tw-flex tw-h-6 tw-min-w-[24px] tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100 tw-text-[10px] tw-font-semibold tw-text-gray-700">
                    {idx + 1}
                  </div>
                  <div className="tw-text-xs tw-text-gray-700 tw-whitespace-pre-wrap tw-leading-relaxed">
                    {renderStepDescriptionWithPills(st, getToolDetails, t)}
                  </div>
                </li>
              ))}
            </ol>
            {selectedTemplate.steps.length > 6 ? (
              <div className="tw-text-[10px] tw-text-gray-400 tw-mt-2">
                {t('config.intention_templates_modal.preview_more', { count: selectedTemplate.steps.length - 6 })}
              </div>
            ) : null}
              </CardMC>
            )}
          </div>
        ) : null}

        {error ? (
          <CardMC className="tw-p-3 tw-bg-white tw-border tw-border-solid tw-border-red-200">
            <div className="tw-text-xs tw-text-red-600">
              {t('config.intention_templates_modal.error_prefix')}
              {' '}
              {String(error)}
            </div>
          </CardMC>
        ) : null}

        <div className="tw-flex tw-justify-end tw-gap-3">
          <ButtonMC
            text={t('buttonTexts.cancel')}
            variant="TEXT_PLAIN"
            className="!tw-text-gray-500"
            onClick={closeAction}
          />
          <ButtonMC
            text={t('config.intention_templates_modal.apply_button')}
            variant="PRIMARY"
            onClick={handleApply}
            disabled={!selectedTemplate || loading || templates.length === 0 || applying}
          />
        </div>
      </div>
    </RightModalLayout>
  );
};

const IntentionTemplateModal = (props) => (
  <AgentProvider agentId={props.agentId}>
    <IntentionTemplateModalContent {...props} />
  </AgentProvider>
);

export default IntentionTemplateModal;

